/**
 * Changes CSS classes if an element is clicked. The configuration is based solely on data attributes.
 *
 * Available data attributes:
 * - [data-w-click-target]: Element selector of the element(s) to change classes on. Supports the following special selectors: & (self), < (parent), << (parentParent), <<< (parentParentParent), + (next)
 * - [data-w-click-class] (optional): Class name(s) to be changed (defaults to "w-is-active").
 * - [data-w-click-method] (optional): Whether to add, remove or toggle the class (defaults to "toggle").
 * - [data-w-click-highlander] (optional): If true, the class will also be removed from sibling elements.
 * - [data-w-click-cancel] (optional): Prevents execution of classClick methods on all parents (see example below).
 * - [data-w-click-no-prevent] (optional): Do not prevent default event execution.
 *
 * Examples:
 * <!-- toggles an element's class -->
 * <button data-w-click-target=".w-menu" data-w-click-class="w-menu--open">Toggle menu</button>
 *
 * <!-- toggles multiple elements' classes -->
 * <button data-w-click-target=".w-menu|.w-sidebar,body" data-w-click-class="w-menu--open|w-menu-is-open">Toggle menu and inform others</button>
 *
 * <!-- adds a class to one element and removes it from another -->
 * <button data-w-click-target=".w-menu|.w-sidebar" data-w-click-class="w-menu--open|w-sidebar--open" data-w-click-method="add|remove">Add and remove</button>
 *
 * <!-- toggle self and parent element's class -->
 * <div><button data-w-click-target="&|<">Toggle self and parent</button></div>
 *
 * <!-- adds a class to list items and only allows one to be active at a time -->
 * <ul>
 *     <li data-w-click-target="&" data-w-click-method="add" data-w-click-highlander>First Item</li>
 *     <li data-w-click-target="&" data-w-click-method="add" data-w-click-highlander>Second Item</li>
 *     <li data-w-click-target="&" data-w-click-method="add" data-w-click-highlander>Third Item</li>
 * </ul>
 *
 * <!-- prevent event bubbling on an element -->
 * <div class="w-modal w-is-active" data-w-click-target="&" data-w-click-method="remove">
 *     <!-- the modal will only be closed when the area around the inner element is clicked -->
 *     <div class="w-modal__inner" data-w-click-cancel>Some Text</div>
 * </div>
 *
 * <!-- do not prevent original event -->
 * <a href="#target" data-w-click-target="#target" data-w-click-no-prevent></a>
 * <!-- browser will still change hash AND trigger classClick -->
 *
 * <!-- remove a class from an element if anything outside it is clicked -->
 * <!-- note that this requires the "checkOutsiders" option to be set to true -->
 * <div class="w-menu" data-w-click-outsider="w-is-open">
 *     <button class="w-menu__trigger" data-w-click-target="<" data-w-click-class="w-is-open">Open</button>
 * </div>
 */

// import deepExtend from './../utilities/deepExtend.js';
import closestParent from './../utilities/closestParent.js';

export default function Click() {
  const options = {
    shortcodes: {
      self: '&',
      parent: '<',
      child: '>',
      next: '+',
      siblings: '~',
      href: '@',
    },
    attributes: {
      target: 'data-w-click-target',
      className: 'data-w-click-class',
      method: 'data-w-click-method',
      highlander: 'data-w-click-highlander',
      cancel: 'data-w-click-cancel',
      noPrevent: 'data-w-click-no-prevent',
      outsider: 'data-w-click-outsider',
    },
    classes: {
      active: 'w-is-active',
      locked: 'w-is-locked',
    },
    checkOutsiders: false,
  };

  /**
     * Fail-safe string split function. Always returns an array (empty if the given string is invalid).
     * @param {string} string
     * @param {string} separator
     * @return {array}
     */
  const _splitString = (string, separator) => {
    if (typeof string === 'string') {
      return string.split(separator);
    } else {
      return [];
    }
  }

  /**
   * Adds, removes or toggles an element's (or a set of elements') class.
   * @param {jQuery} $elements
   * @param {string} [className=w-is-active]
   * @param {string} [method=toggle] - Possible values: add, remove or toggle.
   * @param {boolean} [highlander=false] - If true, this removes the class from the element's siblings.
   * @return {jQuery}
   */
  const _changeClass = (elements, className, method, highlander) => {
    className = className || options.classes.active;
    highlander = highlander !== undefined || false;

    for (const [i, element] of Object.entries(elements)) {

      // remove the class from siblings if highlander is true
      if (highlander && elements.length === 1 && method !== 'remove') {
        const siblings = Array.prototype.filter.call(
          element.parentNode.children,
          function (child) {
            return child !== element;
          }
        );
        for (const sibling of siblings) {
          sibling.classList.remove(className);
        }
      }

      switch (method) {
        case 'add':
          element.classList.add(className) /*.trigger('classclickadd', data)*/;
          break;
        case 'remove':
          element.classList.remove(
            className
          ) /*.trigger('classclickremove', data)*/;
          break;
        default:
          element.classList.toggle(
            className
          ) /*.trigger($element.hasClass(className) ? 'classclickadd' : 'classclickremove', data)*/;
          break;
      }
    }

    return elements;
  }

  return {
    init: () => {
      document.addEventListener(
        'click',
        (event) => {
          // bail if bubble cancel
          if (
            closestParent(
              event.target,
              '[' + options.attributes.cancel + ']'
            )
          )
            return;
  
          const target = closestParent(
            event.target,
            '[' + options.attributes.target + ']'
          );
  
          if (target) {
            const targets = _splitString(
              target.getAttribute(options.attributes.target),
              '|'
            );
            const classNames = _splitString(
              target.getAttribute(options.attributes.className),
              '|'
            );
            const methods = _splitString(
              target.getAttribute(options.attributes.method),
              '|'
            );
            const highlanders = _splitString(
              target.getAttribute(options.attributes.highlander),
              '|'
            );
            const noPrevent = target.getAttribute(
              options.attributes.noPrevent
            )
              ? true
              : false;
  
            // prevent if allowed
            if (!noPrevent) event.preventDefault();
  
            // bail if locked
            if (target.classList.contains(options.classes.locked)) return;
  
            for (const [index, item] of Object.entries(targets)) {
              const split = item.split(/ (.+)?/);
              const shortcode = split[0];
              const selector = split[1];
  
              let targetElements;
  
              switch (shortcode) {
                case options.shortcodes.self:
                  targetElements = selector
                    ? target.querySelectorAll(selector)
                    : [target];
                  break;
                case options.shortcodes.parent:
                  if (selector) {
                    targetElements = [closestParent(target, selector)];
                  } else {
                    targetElements = [target.parentNode];
                  }
                  break;
                case options.shortcodes.child:
                  targetElements = target.children;
                  break;
                case options.shortcodes.next:
                  targetElements = [target.nextElementSibling];
                  break;
                case options.shortcodes.siblings:
                  targetElements = Array.prototype.filter.call(
                    target.parentNode.children,
                    function (child) {
                      return child !== target;
                    }
                  );
                  break;
                case options.shortcodes.href:
                  targetElements = document.querySelectorAll(
                    target.getAttribute('href')
                  );
                  break;
                default:
                  targetElements = document.querySelectorAll(item);
                  break;
              }
  
              _changeClass(
                targetElements,
                classNames[index],
                methods[index],
                highlanders[index]
              );
            }
          }

        }
      );
    }
  };
}
